/**
 * Turn a concrete pathname into a route *template* for use as a RUM pageId.
 *
 *   /en/provider/patientpath/enrollments/3f1c.../metrics
 *     -> /[locale]/provider/patientpath/enrollments/[id]/metrics
 *
 * Why templates rather than raw pathnames: RUM aggregates its metrics by
 * pageId, so recording the raw path would produce one "page" per record and
 * scatter the numbers for a screen across thousands of near-empty buckets —
 * you could never see that "the enrollment metrics screen is slow". It also
 * keeps record identifiers out of the telemetry, which is a standing
 * compliance requirement here.
 *
 * The App Router does not expose the matched route pattern to client
 * components, so this is derived from the pathname. That makes it
 * best-effort: an identifier shape not matched below shows up as pageId
 * sprawl in the RUM console, which is the signal to add a rule here.
 */

import { routing } from "@/i18n/routing";

const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC = /^\d+$/;
/** Opaque hex tokens (object keys, hashes) that aren't dash-formatted UUIDs. */
const LONG_HEX = /^[0-9a-f]{16,}$/i;

/** RUM truncates long page ids; keep them well inside the limit. */
const MAX_LENGTH = 128;

const LOCALES: readonly string[] = routing.locales;

/**
 * Locale shape as it actually appears in the URL: "en-US", "de-DE", and the
 * bare "en" form.
 *
 * Matching a pattern rather than a list is deliberate. There are three
 * disagreeing locale lists in this app -- i18n/routing.ts holds bare codes
 * ("en", "de"), proxy.ts holds the ten lang-COUNTRY codes that actually appear
 * in URLs, and constants/locale.ts holds fifteen as a union type. Binding to
 * any single one re-introduces the bug this replaced, where routing.locales
 * never matched the real "/en-US/..." prefix so the locale was never templated
 * and every screen's metrics fragmented across up to ten locale variants.
 *
 * Only ever applied to the first path segment, and no top-level route in this
 * app is two letters (admin, provider, patient, auth, legal, login, download,
 * expired, maintenance, sso-error, privacy-policy), so there is nothing for it
 * to collide with.
 */
const LOCALE_SHAPE = /^[a-z]{2}(-[a-z]{2})?$/i;

const isLocaleSegment = (segment: string): boolean =>
  LOCALES.includes(segment) || LOCALE_SHAPE.test(segment);

const isIdentifier = (segment: string): boolean =>
  UUID.test(segment) || NUMERIC.test(segment) || LONG_HEX.test(segment);

export function toPageId(pathname: string): string {
  // Strip query/hash defensively — callers pass usePathname() which has
  // neither, but /auth/callback carries an OAuth code in the query string
  // and it must never reach RUM.
  const path = pathname.split(/[?#]/)[0] || "/";

  const segments = path.split("/");
  const templated = segments.map((segment, index) => {
    if (segment === "") {
      return segment;
    }
    // next-intl puts the locale in the first path segment.
    if (index === 1 && isLocaleSegment(segment)) {
      return "[locale]";
    }
    return isIdentifier(segment) ? "[id]" : segment;
  });

  const pageId = templated.join("/") || "/";
  return pageId.length > MAX_LENGTH ? pageId.slice(0, MAX_LENGTH) : pageId;
}
